import { useTranslation } from "@sagot/i18n";
import { Button, ChoiceChips, Field, Input, Select, Skeleton, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { ATTRIBUTE_INPUT_MAX, changedIds, draftOf, hasChoices, itemId, objectsOf, patchesOf, type AttributeItem, type AttributePatches, type Draft, type DraftValue } from "./attribute-values-model";
import { useConversationAttributes, useLeadAttributes, useSaveConversationAttributes, useSaveLeadAttributes } from "./use-attribute-values";

const A = "engage.workflows.attributeValues";

interface InputProps {
  item: AttributeItem;
  value: DraftValue;
  disabled: boolean;
  onChange: (value: DraftValue) => void;
}

/** The control for one attribute, by its type: choices as chips, a boolean as yes / no, a date, a number, or text. */
function ValueControl({ item, value, disabled, onChange }: InputProps) {
  const { t } = useTranslation();
  if (hasChoices(item)) {
    const selected = Array.isArray(value) ? value : [];
    return (
      <fieldset className="min-w-0">
        <legend className="label">{item.label}</legend>
        <ChoiceChips multiple aria-label={item.label} value={selected} onValueChange={onChange} options={item.options.map((option) => ({ value: option, label: option, disabled }))} />
      </fieldset>
    );
  }
  const text = typeof value === "string" ? value : "";
  if (item.type === "bool") {
    return (
      <Field label={item.label}>
        <Select disabled={disabled} value={text} onChange={(e) => { onChange(e.target.value); }}>
          <option value="">{t(`${A}.notSet`)}</option>
          <option value="true">{t("engage.workflows.conditions.yes")}</option>
          <option value="false">{t("engage.workflows.conditions.no")}</option>
        </Select>
      </Field>
    );
  }
  return (
    <Field label={item.label}>
      <Input
        type={item.type === "date" ? "date" : "text"}
        inputMode={item.type === "number" ? "decimal" : undefined}
        maxLength={ATTRIBUTE_INPUT_MAX}
        disabled={disabled}
        placeholder={item.type === "list" ? t(`${A}.listPlaceholder`) : undefined}
        value={text}
        onChange={(e) => { onChange(e.target.value); }}
      />
    </Field>
  );
}

interface EditorProps {
  items: readonly AttributeItem[];
  editable: boolean;
  saving: boolean;
  onSave: (patches: AttributePatches) => Promise<void>;
}

/** Defined attributes grouped by the object they belong to, one Save for everything that changed. Mount it with a `key` that changes with the stored values: the inputs start from them. */
export function AttributeValuesEditor({ items, editable, saving, onSave }: EditorProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(() => draftOf(items));
  const dirty = changedIds(items, draft).length > 0;
  const set = (item: AttributeItem, value: DraftValue) => { setDraft((current) => ({ ...current, [itemId(item)]: value })); };
  return (
    <form
      className="space-y-3"
      data-testid="attribute-values"
      onSubmit={(event) => {
        event.preventDefault();
        void onSave(patchesOf(items, draft));
      }}
    >
      {objectsOf(items).map((object) => (
        <div key={object} className="space-y-2" role="group" aria-label={t(`${A}.objects.${object}`)}>
          <p className="text-xs font-bold uppercase tracking-wider text-fg-subtle">{t(`${A}.objects.${object}`)}</p>
          {items.filter((item) => item.object === object).map((item) => (
            <ValueControl key={itemId(item)} item={item} value={draft[itemId(item)] ?? ""} disabled={!editable || saving} onChange={(value) => { set(item, value); }} />
          ))}
        </div>
      ))}
      {editable ? (
        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" loading={saving} disabled={!dirty}>{t(`${A}.save`)}</Button>
          <span className="text-xs text-fg-subtle">{t(`${A}.workflowHint`)}</span>
        </div>
      ) : null}
    </form>
  );
}

interface BlockProps {
  testId: string;
  query: { isPending: boolean; isError: boolean; data: AttributeItem[] | undefined; refetch: () => unknown };
  save: { isPending: boolean; mutateAsync: (patches: AttributePatches) => Promise<unknown> };
  canEdit: boolean;
}

/** The titled section; a workspace that defined no attribute for these objects sees nothing at all. */
function AttributesBlock({ testId, query, save, canEdit }: BlockProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  if (!query.isPending && !query.isError && query.data?.length === 0) return null;
  const onSave = async (patches: AttributePatches) => {
    try {
      await save.mutateAsync(patches);
      toast.success(t(`${A}.saved`));
    } catch (error) {
      toast.error(message(error));
    }
  };
  return (
    <div data-testid={testId}>
      <div className="label">{t(`${A}.title`)}</div>
      {query.isPending ? <Skeleton className="h-16" aria-hidden="true" /> : null}
      {query.isError ? (
        <div className="space-y-2 text-xs">
          <p className="text-fg-subtle">{t(`${A}.loadFailed`)}</p>
          <Button size="sm" variant="secondary" onClick={() => { void query.refetch(); }}>{t("common.retry")}</Button>
        </div>
      ) : null}
      {query.data && query.data.length > 0 ? <AttributeValuesEditor key={JSON.stringify(query.data)} items={query.data} editable={canEdit} saving={save.isPending} onSave={onSave} /> : null}
    </div>
  );
}

/** The Inbox details panel's attributes: the conversation's and its visitor's. */
export function ConversationAttributesBlock({ conversationId, canEdit }: { conversationId: string; canEdit: boolean }) {
  const query = useConversationAttributes(conversationId);
  const save = useSaveConversationAttributes(conversationId);
  return <AttributesBlock testId="conversation-attributes" query={query} save={save} canEdit={canEdit} />;
}

/** The lead sheet's attributes. */
export function LeadAttributesBlock({ leadId, canEdit }: { leadId: string; canEdit: boolean }) {
  const query = useLeadAttributes(leadId);
  const save = useSaveLeadAttributes(leadId);
  return <AttributesBlock testId="lead-attributes" query={query} save={save} canEdit={canEdit} />;
}
